import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import CalendarDayCell from './CalendarDayCell'
import { rem } from 'polished'
import { calcGridPositionsForVerticalMonth, getCalendarMonthEmptyCells, getLocalDate } from 'lib/datetime/calendarUtils'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import { shouldEnableCalendarRecommendations } from 'selectors/featuresSelectors'

const BaseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7 , 1fr);
`

const Grid = styled(BaseGrid)`
  position: relative;
  margin-top: ${rem(8)};
`

const EmptyCell = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${rem(8)} ${rem(12)};
  background-color: ${props => props.theme.palette.neutral.default.seven};
  box-shadow: 0 -1px 0 0 ${props => props.theme.palette.neutral.default.five},
    1px 0 0 0 ${props => props.theme.palette.neutral.default.five},
    1px 0 0 0 ${props => props.theme.palette.neutral.default.five} inset,
    0 -1px 0 0 ${props => props.theme.palette.neutral.default.five} inset;
`

interface Props {
  onDatesChange: (day: App.BedbankCalendarDay) => void;
  checkIn?: string;
  checkOut?: string;
  days: Array<App.BedbankCalendarDay>;
  className?: string;
  duration: number;
  fetching: boolean;
  flights: boolean;
  enableCalendarRecommendations: boolean;
  adultsNumber: number;
  roomsNumber: number;
  displayPricingAsPerPerson: boolean;
}

function CalendarDaysMonth(props: Props) {
  const {
    className,
    days,
    checkIn,
    checkOut,
    duration,
    fetching,
    flights,
    enableCalendarRecommendations,
    onDatesChange,
    adultsNumber,
    roomsNumber,
    displayPricingAsPerPerson,
  } = props

  const firstDate = days[0]

  const startCellStyle = useMemo(() => ({
    gridColumnStart: getLocalDate(firstDate.checkIn).getDay() + 1,
  }), [firstDate])

  const emptyCells = useMemo(() => getCalendarMonthEmptyCells(firstDate), [firstDate])

  const betweenRange = useMemo(() => {
    if (checkIn && checkOut) {
      return calcGridPositionsForVerticalMonth(
        getLocalDate(firstDate.checkIn),
        getLocalDate(checkIn),
        getLocalDate(checkOut),
      ).range
    }
    return undefined
  }, [checkIn, checkOut, firstDate])

  const onDayClick = useCallback((day: App.BedbankCalendarDay) => {
    onDatesChange(day)
  }, [onDatesChange])

  return (
    <div className={className}>
      <BaseGrid>
        <Caption variant="large" as="abbr" title="Sunday">Sun</Caption>
        <Caption variant="large" as="abbr" title="Monday">Mon</Caption>
        <Caption variant="large" as="abbr" title="Tuesday">Tue</Caption>
        <Caption variant="large" as="abbr" title="Wednesday">Wed</Caption>
        <Caption variant="large" as="abbr" title="Thursday">Thu</Caption>
        <Caption variant="large" as="abbr" title="Friday">Fri</Caption>
        <Caption variant="large" as="abbr" title="Saturday">Sat</Caption>
      </BaseGrid>
      <Grid>
        {emptyCells.map((cell, index) =>
          <EmptyCell style={{ gridColumnStart: cell.column }} key={index}>
            <Heading colour="neutral-four" variant="heading6">{cell.day}</Heading>
          </EmptyCell>,
        )}
        {days.map((day, index) => <CalendarDayCell
          key={day.checkIn}
          duration={duration}
          price={day.price}
          propertyFees={day.propertyFees}
          style={(index === 0 && emptyCells.length === 0) ? startCellStyle : undefined}
          day={day.day}
          fetching={fetching}
          flights={flights}
          flightPrice={day.flightPrice}
          hasPromo={day.hasPromo}
          isCorporate={day.isCorporate}
          checkIn={day.checkIn}
          isCheckIn={day.checkIn === checkIn}
          isCheckOut={day.checkIn === checkOut}
          isBetweenDay={!!(betweenRange && (index + 1) >= betweenRange.start && index < (betweenRange.end - 1))}
          onClick={() => onDayClick(day)}
          enableCalendarRecommendations={enableCalendarRecommendations}
          displayPricingAsPerPerson={displayPricingAsPerPerson}
          adultsNumber={adultsNumber}
          roomsNumber={roomsNumber}
        />)}
      </Grid>
    </div>
  )
}

export default withOfferPageState((state) => ({
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
  enableCalendarRecommendations: shouldEnableCalendarRecommendations(state.bedbankOffer),
}), CalendarDaysMonth)
